import React, { useCallback, useEffect, useState } from 'react';
import { MessageInput } from './MessageInput';
import { MarkdownMessage } from './MarkdownMessage';
import { PromptSuggestionCards } from './promptSuggestions/PromptSuggestionControl';
import { ThemedSpinner } from './ThemedSpinner';
import type { PromptSuggestionsStatus } from '../context/PromptSuggestionsContext';
import type { WelcomeMessageKey } from '../hooks/useChat';
import type {
  PromptSuggestionItem,
  PromptSuggestionsErrorInfo,
} from '../types/promptSuggestions';
import { useTranslation } from '../localization/useAppTranslation';
import '../styles/WelcomePanel.css';

const SUGGESTION_ERROR_VISIBLE_MS = 4000;
const WELCOME_SUGGESTION_PLACEHOLDER_COUNT = 3;

interface WelcomePanelProps {
  welcomeMessageKey: WelcomeMessageKey;
  onSend: (message: string) => void;
  isLoading: boolean;
  composerDraft: string;
  homeSuggestions: PromptSuggestionItem[];
  suggestionError: PromptSuggestionsErrorInfo | null;
  suggestionStatus: PromptSuggestionsStatus;
  onComposerDraftChange: (draft: string) => void;
  onSelectCoachSuggestion: (suggestion: PromptSuggestionItem) => boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

export const WelcomePanel: React.FC<WelcomePanelProps> = ({
  welcomeMessageKey,
  onSend,
  isLoading,
  composerDraft,
  homeSuggestions,
  suggestionError,
  suggestionStatus,
  onComposerDraftChange,
  onSelectCoachSuggestion,
  inputRef
}) => {
  const { t } = useTranslation('chat');

  // Auto-focus input when welcome panel appears
  useEffect(() => {
    if (inputRef?.current) {
      // Small delay ensures DOM is ready
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [inputRef]);

  const handleSend = useCallback((message: string) => {
    if (!message.trim()) return;
    onSend(message);
  }, [onSend]);

  const handleInputChange = (innerHtml: string, textContent: string) => {
    onComposerDraftChange(textContent);
  };

  const hasSuggestions = homeSuggestions.length > 0;

  const [isSuggestionErrorVisible, setIsSuggestionErrorVisible] = useState(false);

  useEffect(() => {
    if (suggestionStatus !== 'error') {
      setIsSuggestionErrorVisible(false);
      return;
    }
    setIsSuggestionErrorVisible(true);
    const timeoutId = setTimeout(() => {
      setIsSuggestionErrorVisible(false);
    }, SUGGESTION_ERROR_VISIBLE_MS);
    return () => clearTimeout(timeoutId);
  }, [suggestionStatus]);

  const isSuggestionsLoading = !hasSuggestions && suggestionStatus === 'loading';
  const shouldRenderSuggestionArea = (
    hasSuggestions || isSuggestionsLoading || isSuggestionErrorVisible
  );

  return (
    <div className="welcome-panel">
      <div className="welcome-panel-content">
        <div className="welcome-panel-message">
          <MarkdownMessage 
            content={t(welcomeMessageKey)}
            className="welcome-panel-text"
          />
        </div>
        <div className="welcome-panel-input-wrapper">
          <MessageInput
            value={composerDraft}
            onChange={handleInputChange}
            onSend={handleSend}
            disabled={isLoading}
            inputRef={inputRef}
          />
        </div>
        {shouldRenderSuggestionArea && (
          <div
            className={`welcome-suggestions-area${isSuggestionsLoading ? ' is-loading' : ''}`}
            aria-busy={isSuggestionsLoading}
          >
            {isSuggestionsLoading && (
              <div
                className="coach-suggestion-cards welcome-suggestion-placeholder-grid"
                aria-hidden="true"
              >
                {Array.from({ length: WELCOME_SUGGESTION_PLACEHOLDER_COUNT }, (_, index) => (
                  <div key={index} className="welcome-suggestion-placeholder" />
                ))}
              </div>
            )}
            {isSuggestionsLoading && (
              <div className="welcome-suggestions-status" role="status">
                <ThemedSpinner size="sm" ariaLabel={t('suggestions.loading')} />
                <span>{t('suggestions.loading')}</span>
              </div>
            )}
            {!hasSuggestions && isSuggestionErrorVisible && (
              <div className="welcome-suggestions-status welcome-suggestions-error" role="alert">
                <span>{suggestionError?.detail || t('suggestions.unavailable')}</span>
              </div>
            )}
            <PromptSuggestionCards
              className="welcome-suggestion-cards"
              suggestions={homeSuggestions}
              maxSuggestions={WELCOME_SUGGESTION_PLACEHOLDER_COUNT}
              disabled={isLoading}
              onSelect={onSelectCoachSuggestion}
            />
          </div>
        )}
      </div>
    </div>
  );
};
